import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { IconoirMessage, IconoirXmark } from '@ey-xd/motif-icon';
import {
  MotifButtonModule,
  MotifFormsModule,
  MotifIconModule,
} from '@ey-xd/ng-motif';
import { FeedbackService } from 'app/core/service/feedback/feedback.service';
import { ToastService } from 'app/core/service/toast/toast.service';
import {
  sameSurveyKey,
  Survey,
  SurveyKey,
  SurveyQuestion,
  SurveyQuestionOption,
  SurveyQuestionType,
  SurveyTextType,
} from 'app/models/feedback-question';
import {
  SurveyQuestionAnswer,
  SurveyQuestionAnswerOption,
  SurveyResponse,
} from 'app/models/survey-response';
import { take } from 'rxjs';
import { FeedbackQuestionComponent } from '../feedback-question/feedback-question.component';

@Component({
  selector: 'app-feedback',
  standalone: true,
  imports: [
    CommonModule,
    MotifIconModule,
    ReactiveFormsModule,
    MotifFormsModule,
    MotifButtonModule,
    FeedbackQuestionComponent,
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
})
export class FeedbackComponent implements OnChanges {
  IconoirMessage = IconoirMessage;
  IconoirXmark = IconoirXmark;

  surveyForm?: FormGroup;

  private _questionFormGroups?: FormGroup[];
  get questionFormGroups(): FormGroup[] | undefined {
    if (!this.surveyForm) {
      return undefined;
    }

    if (!this._questionFormGroups) {
      this._questionFormGroups = (this.surveyForm.get('questions') as FormArray)
        .controls as FormGroup[];
    }

    return this._questionFormGroups;
  }

  expanded: boolean = false;
  lateralExpand: boolean = false;

  survey?: Survey;

  get questions(): SurveyQuestion[] {
    return this.survey?.questions || [];
  }

  labelText: string = 'Help us improve!';

  @Input() surveyKey?: SurveyKey;
  private prevSurveyKey?: SurveyKey;

  isSubmitDisabled: boolean = true;

  constructor(
    private formBuilder: FormBuilder,
    private feedbackService: FeedbackService,
    private toastService: ToastService
  ) {}

  /**Functions to manipulate events */
  expandFeedbackBox() {
    if (this.lateralExpand) {
      this.lateralExpand = false;
    }
    this.expanded = true;
  }

  closeFeedbackBox() {
    this.expanded = false;
  }

  onHoverText() {
    if (!this.expanded) {
      this.lateralExpand = true;
    }
  }

  onHoverOut() {
    this.lateralExpand = false;
  }
  /** */

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['surveyKey']) {
      const surveyKey: SurveyKey = changes['surveyKey'].currentValue;
      if (surveyKey && !sameSurveyKey(surveyKey, this.prevSurveyKey)) {
        this.surveyKey = surveyKey;
        this.prevSurveyKey = surveyKey;

        if (this.surveyKey) {
          this.getSurvey();
        }
      }
    }
  }

  private getSurvey() {
    this.feedbackService
      .getSurvey(this.surveyKey!)
      .pipe(take(1))
      .subscribe({
        next: (survey: Survey) => {
          this.survey = survey;
          this.setupSurveyForm();
        },
        error: (error: HttpErrorResponse) => {
          const message: string =
            error.error.message ||
            'Unexpected error when fetching survey. If the error persist, please contact servicedesk@ey.com';
          this.toastService.showToast('error', `${message}`, {
            classTheme: '',
            autoHide: false,
          });
        },
      });
  }

  private setupSurveyForm(): void {
    const fb = this.formBuilder;

    const questionsFormArray = fb.array([]) as FormArray;

    for (const question of this.questions) {
      const questionFormControls: { [key: string]: AbstractControl } = {};

      if (
        question.type == SurveyQuestionType.TEXT ||
        question.type == SurveyQuestionType.CHOICE_AND_TEXT
      ) {
        questionFormControls['textInput'] = fb.control(
          '',
          question.is_required ? [Validators.required] : []
        );
      }

      if (
        question.type == SurveyQuestionType.CHOICE ||
        question.type == SurveyQuestionType.CHOICE_AND_TEXT
      ) {
        questionFormControls['options'] = this.createQuestionOptionsFormArray(
          question.options || []
        );
      }

      questionsFormArray.push(fb.group(questionFormControls));
    }

    this.surveyForm = fb.group({
      questions: questionsFormArray,
    });

    this.surveyForm.valueChanges.subscribe(() => {
      this.onQuestionAnswersChanged();
    });

    this.checkIsSubmitDisabled();
  }

  private createQuestionOptionsFormArray(
    options: SurveyQuestionOption[]
  ): FormArray {
    const fb = this.formBuilder;
    const optionsFormArray = fb.array([]) as FormArray;

    for (const option of options) {
      let optionFormGroup: FormGroup;
      const optionFormGroupControls: { [key: string]: AbstractControl } = {};

      const optionCheckedControl = fb.control(false);
      optionFormGroupControls['optionChecked'] = optionCheckedControl;

      switch (option.text_input_type) {
        case SurveyTextType.MANDATORY:
        case SurveyTextType.OPTIONAL:
          const optionTextInputControl = fb.control(
            '',
            option.text_input_type === SurveyTextType.MANDATORY
              ? [
                  this.createMandatoryOptionTextInputValidator(
                    optionCheckedControl
                  ),
                ]
              : []
          );

          optionCheckedControl.valueChanges.subscribe(
            (checked: boolean | null) => {
              optionTextInputControl.updateValueAndValidity();
            }
          );

          optionFormGroupControls['textInput'] = optionTextInputControl;

          break;
      }

      optionFormGroup = fb.group(optionFormGroupControls);

      optionsFormArray.push(optionFormGroup!);
    }

    return optionsFormArray;
  }

  private createMandatoryOptionTextInputValidator(
    optionCheckedControl: FormControl
  ): ValidatorFn {
    return (
      optionTextInputControl: AbstractControl
    ): ValidationErrors | null => {
      if (optionCheckedControl.value) {
        return Validators.required(optionTextInputControl);
      } else {
        return null;
      }
    };
  }

  onQuestionAnswersChanged(): void {
    this.checkIsSubmitDisabled();
  }

  private checkIsSubmitDisabled(): void {
    if (!this.surveyForm) {
      this.isSubmitDisabled = true;
      return;
    }

    if (this.formHasRequiredChoiceQuestionUnanswered()) {
      this.isSubmitDisabled = true;
      console.log(
        'Form has required choice question unanswered. Submit disabled.'
      );
      return;
    }

    this.isSubmitDisabled = this.surveyForm.invalid;
  }

  private formHasRequiredChoiceQuestionUnanswered(): boolean {
    if (!this.surveyForm) {
      return true; // If the form is not initialized, we consider it invalid
    }

    const questionsFormArray = this.surveyForm.get('questions') as FormArray;

    for (let i = 0; i < questionsFormArray.length; i++) {
      const questionFormGroup = questionsFormArray.at(i) as FormGroup;
      const question: SurveyQuestion = this.questions[i];

      if (
        question.type == SurveyQuestionType.CHOICE ||
        question.type == SurveyQuestionType.CHOICE_AND_TEXT
      ) {
        const optionsFormArray = questionFormGroup.get('options') as FormArray;

        if (question.is_required) {
          if (!optionsFormArray || !optionsFormArray.length) {
            return true;
          }

          let hasCheckedOption = false;

          CheckIfOneOptionChecked: for (const optionFormGroup of optionsFormArray.controls) {
            if (optionFormGroup.get('optionChecked')?.value) {
              hasCheckedOption = true;
              break CheckIfOneOptionChecked;
            }
          }

          if (!hasCheckedOption) {
            return true;
          }
        }
      }
    }

    return false;
  }

  submitFeedback() {
    this.toastService.clearToastWithTag(true);

    const surveyResponse = this.surveyResponse;
    if (!surveyResponse) {
      console.log('No survey response available.');
      return;
    }

    if (!this.surveyKey) {
      console.log('No survey key available.');
      return;
    } else {
      // disable submit button before request to avoid another click
      this.isSubmitDisabled = true;

      this.feedbackService
        .postFeedback(this.surveyKey, surveyResponse)
        .pipe(take(1))
        .subscribe({
          next: (data: any) => {
            //manage the feedback response here
            const message: string = 'Feedback submitted successfully';
            this.toastService.showToast('success', `${message}`, {
              classTheme: '',
              autoHide: false,
            });

            this.expanded = false;
            this.isSubmitDisabled = false;
            this.surveyForm?.reset();
          },
          error: (error: HttpErrorResponse) => {
            const message: string =
              error.error.message ||
              'Unexpected error when submitting feedback. If the error persist, please contact servicedesk@ey.com';
            this.toastService.showToast('error', `${message}`, {
              classTheme: '',
              autoHide: false,
            });
            this.isSubmitDisabled = false;
          },
        });
    }
  }

  get surveyResponse(): SurveyResponse | undefined {
    if (!this.surveyForm) {
      return undefined;
    } else {
      const questionsFormArray = this.surveyForm.get('questions') as FormArray;
      const questions: SurveyQuestion[] = this.questions || [];

      const answers = questions.map((question, index) => {
        const questionFormGroup = questionsFormArray.at(index) as FormGroup;

        const questionAnswer: SurveyQuestionAnswer = {
          question_id: question.id,
        };

        if (
          question.type == SurveyQuestionType.TEXT ||
          question.type == SurveyQuestionType.CHOICE_AND_TEXT
        ) {
          questionAnswer.text_response =
            questionFormGroup.get('textInput')?.value;
        }

        if (
          question.type == SurveyQuestionType.CHOICE ||
          question.type == SurveyQuestionType.CHOICE_AND_TEXT
        ) {
          questionAnswer.selected_options = [];

          if (question.options) {
            const optionsFormArray = questionFormGroup.get(
              'options'
            ) as FormArray;

            for (let i = 0; i < question.options.length; i++) {
              const option = question.options[i];

              const optionFormGroup = optionsFormArray.at(i) as FormGroup;
              const optionChecked = optionFormGroup.get('optionChecked')?.value;

              if (optionChecked) {
                const selectedOption: SurveyQuestionAnswerOption = {
                  option_id: option.id,
                };

                selectedOption.text_response =
                  optionFormGroup.get('textInput')?.value;

                questionAnswer.selected_options.push(selectedOption);
              }
            }
          }
        }

        return questionAnswer;
      });

      return {
        answers,
      };
    }
  }
}
